import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { hole } from '../lib/api'
import type { MitgliedKurz } from '../lib/modelle'
import { mitgliedsFarbe } from '../session/logic'
import { Avatar, Button, Empty, ListItem } from '../ui'

interface Eintrag {
  id: string
  module: string
  kind: string
  payload: Record<string, unknown>
  actorMemberId: string | null
  subjectType: string | null
  subjectId: string | null
  createdAt: string
}

interface FeedAntwort {
  eintraege: Eintrag[]
  naechsteVor?: string
}

/**
 * Der Satz wird HIER gebildet, nicht in der Datenbank.
 *
 * Der Server speichert `kind` + `payload` und nie einen fertigen deutschen
 * Satz: sonst wäre der Feed nicht übersetzbar, und eine spätere Textänderung
 * fälschte die Vergangenheit. Ein unbekanntes `kind` bekommt eine ehrliche
 * Ersatzformulierung statt einer leeren Zeile.
 */
function satzFuer(e: Eintrag, wer: string): { text: string; gegenstand: string | null } {
  const titel = typeof e.payload.title === 'string' ? e.payload.title : null
  switch (e.kind) {
    case 'tasks.completed':
      return { text: `${wer} hat erledigt`, gegenstand: titel }
    case 'tasks.created':
      return { text: `${wer} hat eine Aufgabe angelegt`, gegenstand: titel }
    case 'calendar.created':
      return { text: `${wer} hat einen Termin angelegt`, gegenstand: titel }
    case 'shopping.list_created':
      return { text: `${wer} hat eine Einkaufsliste angelegt`, gegenstand: titel }
    case 'shopping.list_archived':
      return { text: `${wer} hat eine Einkaufsliste archiviert`, gegenstand: titel }
    case 'shopping.item_created':
      return { text: `${wer} hat zum Einkauf hinzugefügt`, gegenstand: titel }
    case 'shopping.item_changed':
      return {
        text:
          e.payload.checked === true
            ? `${wer} hat eingepackt`
            : e.payload.checked === false
              ? `${wer} hat wieder geöffnet`
              : `${wer} hat einen Einkaufsartikel geändert`,
        gegenstand: titel,
      }
    case 'shopping.items_reordered':
      return { text: `${wer} hat die Einkaufsliste sortiert`, gegenstand: null }
    case 'meals.recipe_created':
      return { text: `${wer} hat ein Rezept angelegt`, gegenstand: titel }
    case 'meals.planned':
      return { text: `${wer} hat eine Mahlzeit eingeplant`, gegenstand: titel }
    case 'meals.to_shopping_list':
      return { text: `${wer} hat Zutaten auf die Einkaufsliste gesetzt`, gegenstand: titel }
    case 'timetable.created':
      return { text: `${wer} hat einen Stundenplan angelegt`, gegenstand: titel }
    case 'timetable.changed':
      return { text: `${wer} hat einen Stundenplan geändert`, gegenstand: titel }
    default:
      return { text: `${wer}: ${e.kind}`, gegenstand: titel }
  }
}

function zeitpunkt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function Aktivitaet() {
  const [vor, setVor] = useState<string | null>(null)
  const [gesammelt, setGesammelt] = useState<Eintrag[]>([])

  const pfad = vor
    ? `/api/activity?limit=30&vor=${encodeURIComponent(vor)}`
    : '/api/activity?limit=30'
  const feed = useQuery({
    queryKey: ['activity', vor],
    queryFn: () => hole<FeedAntwort>(pfad),
  })
  const mitglieder = useQuery({
    queryKey: ['members'],
    queryFn: () => hole<MitgliedKurz[]>('/api/members'),
  })

  const personen = new Map((mitglieder.data ?? []).map((m) => [m.id, m]))
  // Die erste Seite kommt aus der Abfrage, weitere werden angehängt. Ein
  // Duplikat kann durch einen neuen Eintrag zwischen zwei Seiten entstehen -
  // die id filtert es heraus.
  const gesehen = new Set(gesammelt.map((e) => e.id))
  const sichtbar = [...gesammelt, ...(feed.data?.eintraege ?? []).filter((e) => !gesehen.has(e.id))]
  const mehr = feed.data?.naechsteVor

  if (feed.isError)
    return (
      <p className="fehler" role="alert">
        Die Daten konnten nicht geladen werden.{' '}
        <Button
          variant="text"
          onClick={() => {
            void feed.refetch()
          }}
        >
          Erneut versuchen
        </Button>
      </p>
    )

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Aktivität</h1>
          <p>Was in der Familie passiert ist.</p>
        </div>
      </div>

      {feed.isPending && sichtbar.length === 0 ? (
        <p className="unterzeile">Wird geladen …</p>
      ) : sichtbar.length === 0 ? (
        <Empty
          titel="Noch nichts passiert"
          text="Sobald jemand eine Aufgabe abhakt oder einen Termin einträgt, steht es hier."
        />
      ) : (
        <>
          <ul className="liste liste--gruppe">
            {sichtbar.map((e) => {
              const person = e.actorMemberId ? personen.get(e.actorMemberId) : undefined
              const wer = person?.displayName ?? 'Jemand'
              const farbe = person ? mitgliedsFarbe(person.color, person.id) : undefined
              const { text, gegenstand } = satzFuer(e, wer)
              return (
                <ListItem
                  key={e.id}
                  titel={`${text}${gegenstand ? `: ${gegenstand}` : ''}`}
                  unterzeile={zeitpunkt(e.createdAt)}
                  farbe={farbe}
                  fuehrend={person ? <Avatar name={wer} farbe={farbe ?? ''} /> : undefined}
                />
              )
            })}
          </ul>
          {mehr ? (
            <div className="reihe">
              <Button
                variant="tonal"
                onClick={() => {
                  setGesammelt(sichtbar)
                  setVor(mehr)
                }}
              >
                Mehr laden
              </Button>
            </div>
          ) : null}
        </>
      )}
    </>
  )
}
